/**
 * Post-event award builder (성적 산출 / 시상, CLAUDE.md §15 #455).
 *
 * The organiser builds the award sheet after the event from one data table (`AwardTable`, every
 * stat of every APPROVED individual) and a saved plan (`AwardPlan`: ordered divisions, each with a
 * filter, a sort and an ordered list of awards). Winners are derived by the pure
 * `computeAwardSheet(table, plan)` (src/awards-plan/compute.ts) on the server and in the browser —
 * the plan never changes results, so it can be edited after FINALIZED.
 */
import { z } from 'zod';
import { Gender, PlayStatus } from '../enums';
import { I18nText } from '../i18n';

// ------------------------------------------------------------------------------------ plan

export const AwardKind = z.enum([
  /** n-th place of the division's sort (param = n) */
  'RANK',
  /** best (lowest) gross */
  'MEDALIST',
  /** best (lowest) net */
  'BEST_NET',
  /** longest drive (param.contestId or any longest-drive contest) */
  'LONGEST',
  /** nearest to the pin (param.contestId or any nearest-pin contest) */
  'NEAREST',
  /** most birdie-or-better holes */
  'MOST_BIRDIES',
  'MOST_PARS',
  /** most bogey-or-worse holes (fun prize) */
  'MOST_BOGEYS',
  /** most eagle-or-better holes */
  'MOST_EAGLES',
  /** fewest putts (only cards with putts on every played hole) */
  'FEWEST_PUTTS',
  /** lucky place n of the division's sort (param = n) */
  'LUCKY_RANK',
  /** second last of the division's sort (부비상) */
  'BOOBY',
  /** last of the division's sort (최하위) */
  'LAST',
  /** every hole-in-one of the division */
  'HOLE_IN_ONE',
]);
export type AwardKind = z.infer<typeof AwardKind>;

/** kinds that pick a place of the division's ordering (need `n` for RANK / LUCKY_RANK) */
export const POSITIONAL_AWARD_KINDS: readonly AwardKind[] = ['RANK', 'LUCKY_RANK', 'BOOBY', 'LAST'];

/** NEW_PERIO / SYSTEM36: same-day handicaps computed at results time from the cards (§15 #464) */
export const AwardSortBasis = z.enum(['GROSS', 'NET', 'POINTS', 'TO_PAR', 'NEW_PERIO', 'SYSTEM36']);
export type AwardSortBasis = z.infer<typeof AwardSortBasis>;

export const AwardPlanAwardSchema = z.object({
  /**
   * stable key of the award inside its division (overrides point at it); the server assigns one
   * on save when absent — older plans address awards by `#<index>` until then (§15 #478)
   */
  id: z.string().min(1).max(40).optional(),
  kind: AwardKind,
  /** custom label; null/absent = the kind's default text in the UI language */
  label: z.string().trim().max(80).nullable().optional(),
  /** overrides the plan's duplicate rule for this award (true also crosses divisions) */
  allowDuplicate: z.boolean().nullable().optional(),
  /** RANK / LUCKY_RANK place (1-based) */
  n: z.number().int().min(1).max(500).nullable().optional(),
  /** LONGEST / NEAREST: one contest; null = best over every contest of that type */
  contestId: z.uuid().nullable().optional(),
});
export type AwardPlanAward = z.infer<typeof AwardPlanAwardSchema>;

export const AwardDivisionFilterSchema = z.object({
  /** category key -> accepted values (AND across keys, OR within a key; empty = any) */
  categories: z.record(z.string(), z.array(z.string())).default({}),
  gender: Gender.nullable().optional(),
  /** true = pros only, false = amateurs only */
  isPro: z.boolean().nullable().optional(),
  nationality: z.array(z.string()).nullable().optional(),
  ageMin: z.number().int().min(0).max(120).nullable().optional(),
  ageMax: z.number().int().min(0).max(120).nullable().optional(),
  /** default: every status (positional awards still rank ACTIVE cards only) */
  playStatus: z.array(PlayStatus).nullable().optional(),
});
export type AwardDivisionFilter = z.infer<typeof AwardDivisionFilterSchema>;

export const AwardDivisionSortSchema = z.object({
  basis: AwardSortBasis,
  /** one round only; null = whole tournament */
  roundNo: z.number().int().min(1).nullable().optional(),
});
export type AwardDivisionSort = z.infer<typeof AwardDivisionSortSchema>;

/**
 * How equal records are separated for positional awards (§15 #478):
 * COUNTBACK = leaderboard rank of the basis (back nine / 6 / 3 / 1, then bib) — the default;
 * SHARED = everyone on the same record shares the award (T2, T2 → no 3rd);
 * PLAYOFF = the order the organiser picks (`playoff`), bib until then (flagged);
 * HANDICAP_LOW = the lower handicap first, then bib.
 */
export const AwardTieRule = z.enum(['COUNTBACK', 'SHARED', 'PLAYOFF', 'HANDICAP_LOW']);
export type AwardTieRule = z.infer<typeof AwardTieRule>;

export const AwardDivisionSchema = z.object({
  id: z.string().min(1).max(40),
  name: z.string().trim().min(1).max(80),
  filter: AwardDivisionFilterSchema,
  sort: AwardDivisionSortSchema,
  /** priority order: earlier awards are decided first */
  awards: z.array(AwardPlanAwardSchema).max(60),
  /** absent = COUNTBACK — additive */
  tieRule: AwardTieRule.optional(),
  /** PLAYOFF: participant ids in the order the organiser decided (earlier = better) — additive */
  playoff: z.array(z.uuid()).max(500).optional(),
});
export type AwardDivision = z.infer<typeof AwardDivisionSchema>;

/**
 * A winner set by hand (수동 조정, §15 #478): replaces the computed winners of one award; an empty
 * list = no winner. The reason is required and audited (`tournament.award_plan.override`).
 */
export const AwardOverrideSchema = z.object({
  divisionId: z.string().min(1).max(40),
  /** `award.id`, or `#<index>` for an award saved before ids existed */
  awardKey: z.string().min(1).max(40),
  winners: z.array(z.uuid()).max(20),
  reason: z.string().trim().min(1).max(300),
  /** set by the server on save */
  at: z.iso.datetime().nullable().optional(),
  byName: z.string().max(120).nullable().optional(),
});
export type AwardOverride = z.infer<typeof AwardOverrideSchema>;

export const AwardPlanSchema = z.object({
  /** default for every award: a winner of an earlier award may win again */
  allowDuplicates: z.boolean().default(false),
  /** winners of earlier divisions are skipped in later ones (division order = priority) */
  divisionsExclusive: z.boolean().default(true),
  divisions: z.array(AwardDivisionSchema).max(30).default([]),
  /** WD / DNS / DQ players never win (stat and contest awards too); absent = true — additive */
  excludeWithdrawn: z.boolean().optional(),
  /** winners set by hand — additive */
  overrides: z.array(AwardOverrideSchema).max(200).optional(),
});
export type AwardPlan = z.infer<typeof AwardPlanSchema>;

export const EMPTY_AWARD_PLAN: AwardPlan = {
  allowDuplicates: false,
  divisionsExclusive: true,
  divisions: [],
};

/** PUT body: the plan plus the version the editor started from (409 AWARD_PLAN_CONFLICT) */
export const AwardPlanPutSchema = AwardPlanSchema.extend({
  /** absent (older apps) = no conflict check */
  expectedVersion: z.number().int().min(0).optional(),
});
export type AwardPlanPut = z.infer<typeof AwardPlanPutSchema>;

// ------------------------------------------------------------------------------------ table

export const AwardTableRoundSchema = z.object({
  roundNo: z.number(),
  gross: z.number().nullable(),
  net: z.number().nullable(),
  points: z.number().nullable(),
  toPar: z.number().nullable(),
  holesPlayed: z.number(),
  /** 신페리오 net of this round (complete card only) — additive */
  newPerioNet: z.number().nullable().optional(),
  /** 시스템36 net of this round (complete card only) — additive */
  system36Net: z.number().nullable().optional(),
});

/** a same-day handicap computed from the card: handicap and net (gross − handicap) */
export const AwardAltScoreSchema = z.object({ handicap: z.number(), net: z.number() });

export const AwardTableRowSchema = z.object({
  participantId: z.uuid(),
  bib: z.number().nullable(),
  name: z.string(),
  gender: Gender.nullable(),
  isPro: z.boolean(),
  birthYear: z.number().nullable(),
  /** registration age, else tournament start year − birth year */
  age: z.number().nullable(),
  nationality: z.string().nullable(),
  /** category key -> value */
  categories: z.record(z.string(), z.string()),
  playStatus: PlayStatus,
  holesPlayed: z.number(),
  gross: z.number().nullable(),
  net: z.number().nullable(),
  points: z.number().nullable(),
  toPar: z.number().nullable(),
  rounds: z.array(AwardTableRoundSchema),
  /** position label of the primary view ("1", "T3", "WD", "-") */
  positionLabel: z.string(),
  /** leaderboard rank (countback applied) per sort basis; absent = no such view / not ranked */
  rankBy: z.partialRecord(AwardSortBasis, z.number()),
  counts: z.object({
    /** eagle or better */
    eagles: z.number(),
    birdies: z.number(),
    pars: z.number(),
    bogeys: z.number(),
    /** double bogey or worse (incl. picked-up holes) */
    doubles: z.number(),
    aces: z.number(),
  }),
  /** 신페리오 over the played rounds (null until every played card is complete) — additive */
  newPerio: AwardAltScoreSchema.nullable().optional(),
  /** 시스템36 over the played rounds — additive */
  system36: AwardAltScoreSchema.nullable().optional(),
  /** handicap used by HANDICAP_LOW ties: fixed handicap, else handicap index — additive */
  handicapIndex: z.number().nullable().optional(),
  /** total putts over holes with putts entered */
  putts: z.number().nullable(),
  /** holes with putts entered */
  puttsHoles: z.number(),
  /** best VALID distance per contest the player has an entry in */
  contests: z.array(
    z.object({
      contestId: z.uuid(),
      distanceMeters: z.number(),
      /** server time of that entry (earlier wins a tie) */
      at: z.iso.datetime(),
    }),
  ),
});
export type AwardTableRow = z.infer<typeof AwardTableRowSchema>;

export const AwardTableContestSchema = z.object({
  contestId: z.uuid(),
  type: z.enum(['NEAREST_PIN', 'LONGEST_DRIVE']),
  name: z.string().nullable(),
  holeNumber: z.number(),
  roundNo: z.number().nullable(),
});
export type AwardTableContest = z.infer<typeof AwardTableContestSchema>;

export const AwardTableCategorySchema = z.object({
  key: z.string(),
  label: I18nText,
  options: z.array(z.object({ value: z.string(), label: I18nText })),
});

export const AwardTableSchema = z.object({
  tournamentId: z.uuid(),
  /** FINAL snapshot when FINALIZED, else live standings */
  kind: z.enum(['LIVE', 'FINAL']),
  generatedAt: z.iso.datetime(),
  /** sort bases this tournament has a ranked view for (others sort by value + bib) */
  bases: z.array(AwardSortBasis),
  primaryBasis: AwardSortBasis,
  roundNos: z.array(z.number()),
  /** 신페리오 hidden holes drawn for the results, per round number — additive */
  newPerioHiddenHoles: z.record(z.string(), z.array(z.number())).optional(),
  categories: z.array(AwardTableCategorySchema),
  contests: z.array(AwardTableContestSchema),
  rows: z.array(AwardTableRowSchema),
});
export type AwardTable = z.infer<typeof AwardTableSchema>;

// ------------------------------------------------------------------------------------ sheet

export const AwardWinnerSchema = z.object({
  participantId: z.uuid(),
  name: z.string(),
  bib: z.number().nullable(),
  /** the record that won: score, count, putts or distance (m) */
  value: z.number().nullable(),
});
export type AwardWinner = z.infer<typeof AwardWinnerSchema>;

/** why a better-placed player did not get an award (preview only, never public) */
export const AwardSkipReason = z.enum(['EARLIER_DIVISION', 'EARLIER_AWARD']);
export const AwardSkippedSchema = AwardWinnerSchema.extend({ reason: AwardSkipReason });
export type AwardSkipped = z.infer<typeof AwardSkippedSchema>;

/** how a tie on the record was separated for this award */
export const AwardDecidedBy = z.enum(['COUNTBACK', 'HANDICAP_LOW', 'PLAYOFF', 'BIB', 'SHARED']);
export type AwardDecidedBy = z.infer<typeof AwardDecidedBy>;

export const AwardSheetContestSchema = z.object({
  contestId: z.uuid(),
  type: z.enum(['NEAREST_PIN', 'LONGEST_DRIVE']),
  name: z.string().nullable(),
  holeNumber: z.number(),
  roundNo: z.number().nullable(),
});

export const AwardSheetAwardSchema = z.object({
  index: z.number(),
  /** `award.id` or `#<index>` (overrides) */
  key: z.string().optional(),
  award: AwardPlanAwardSchema,
  winners: z.array(AwardWinnerSchema),
  /** winners were set by hand */
  manual: z.boolean().optional(),
  overrideReason: z.string().nullable().optional(),
  /** what the rules picked when an override replaced it */
  computedWinners: z.array(AwardWinnerSchema).optional(),
  /** the record was tied with the next player and this decided it */
  decidedBy: AwardDecidedBy.optional(),
  /** PLAYOFF rule: the tie is not ordered yet (bib decides meanwhile) */
  needsPlayoff: z.boolean().optional(),
  /** better-placed players left out by the duplicate rules (max 5) */
  skipped: z.array(AwardSkippedSchema).optional(),
  /** LONGEST / NEAREST: the contest the winning entry belongs to */
  contest: AwardSheetContestSchema.optional(),
});
export type AwardSheetAward = z.infer<typeof AwardSheetAwardSchema>;

/** players on the same record around an awarded place (PLAYOFF rule: the organiser orders them) */
export const AwardTieGroupSchema = z.object({
  value: z.number(),
  players: z.array(AwardWinnerSchema),
  /** every player of the group is already in `division.playoff` */
  ordered: z.boolean(),
});
export type AwardTieGroup = z.infer<typeof AwardTieGroupSchema>;

export const AwardSheetDivisionSchema = z.object({
  divisionId: z.string(),
  name: z.string(),
  sort: AwardDivisionSortSchema,
  tieRule: AwardTieRule.optional(),
  /** players matching the filter */
  playerCount: z.number(),
  awards: z.array(AwardSheetAwardSchema),
  /** tied records around awarded places (all tie rules; PLAYOFF needs them ordered) */
  ties: z.array(AwardTieGroupSchema).optional(),
});
export type AwardSheetDivision = z.infer<typeof AwardSheetDivisionSchema>;

export const AwardSheetSchema = z.object({ divisions: z.array(AwardSheetDivisionSchema) });
export type AwardSheet = z.infer<typeof AwardSheetSchema>;

// ------------------------------------------------------------------- state (§15 #478)

/** the sheet frozen by 「시상 확정」: public pages show it even when scores or the plan change */
export const AwardFinalizedSchema = z.object({
  at: z.iso.datetime(),
  byName: z.string().nullable(),
  /** LIVE = frozen before the results were FINALIZED */
  tableKind: z.enum(['LIVE', 'FINAL']),
  planVersion: z.number().int(),
  sheet: AwardSheetSchema,
});
export type AwardFinalized = z.infer<typeof AwardFinalizedSchema>;

export const AwardPlanResponseSchema = z.object({
  plan: AwardPlanSchema,
  updatedAt: z.iso.datetime().nullable(),
  /** bumped on every save / finalize / publish (PUT expectedVersion) — additive */
  version: z.number().int().optional(),
  /** spectators can see the award sheet (GET /public/t/:token/award-sheet) — additive */
  published: z.boolean().optional(),
  finalized: AwardFinalizedSchema.nullable().optional(),
});
export type AwardPlanResponse = z.infer<typeof AwardPlanResponseSchema>;

export const AwardFinalizeRequestSchema = z.object({
  expectedVersion: z.number().int().min(0).optional(),
});
export const AwardFinalizeResponseSchema = AwardPlanResponseSchema.extend({
  /** TOURNAMENT_NOT_FINALIZED: frozen from live standings (allowed, warned) */
  warnings: z.array(z.string()),
});
export const AwardUnfinalizeRequestSchema = z.object({
  reason: z.string().trim().min(1).max(300),
});
export const AwardPublishRequestSchema = z.object({ published: z.boolean() });

// ------------------------------------------------------------------- public sheet + ceremony

/** public winner: name and bib only (no participant ids) */
export const PublicAwardWinnerSchema = z.object({
  name: z.string(),
  bib: z.number().nullable(),
  value: z.number().nullable(),
});
export type PublicAwardWinner = z.infer<typeof PublicAwardWinnerSchema>;

export const PublicAwardSheetAwardSchema = z.object({
  index: z.number(),
  award: AwardPlanAwardSchema.pick({ kind: true, label: true, n: true }),
  winners: z.array(PublicAwardWinnerSchema),
  decidedBy: AwardDecidedBy.optional(),
  contest: AwardSheetContestSchema.omit({ contestId: true }).optional(),
});
export type PublicAwardSheetAward = z.infer<typeof PublicAwardSheetAwardSchema>;

export const PublicAwardSheetDivisionSchema = z.object({
  name: z.string(),
  sort: AwardDivisionSortSchema,
  awards: z.array(PublicAwardSheetAwardSchema),
});
export type PublicAwardSheetDivision = z.infer<typeof PublicAwardSheetDivisionSchema>;

/** the ceremony slide spectators / TV follow (POST /tournaments/:tid/award-ceremony) */
export const AwardCeremonyStateSchema = z.object({
  active: z.boolean(),
  /** index into `ceremonySlides(sheet)` */
  index: z.number().int().min(0),
  updatedAt: z.iso.datetime(),
});
export type AwardCeremonyState = z.infer<typeof AwardCeremonyStateSchema>;
export const AwardCeremonyRequestSchema = z.object({
  active: z.boolean(),
  index: z.number().int().min(0).max(2000),
});

export const PublicAwardSheetSchema = z.object({
  tournamentName: z.string(),
  /** true = frozen by 「시상 확정」; false = live computation (show "unofficial") */
  official: z.boolean(),
  finalizedAt: z.iso.datetime().nullable(),
  generatedAt: z.iso.datetime(),
  divisions: z.array(PublicAwardSheetDivisionSchema),
  ceremony: AwardCeremonyStateSchema.nullable(),
});
export type PublicAwardSheet = z.infer<typeof PublicAwardSheetSchema>;

/** admin GET /tournaments/:tid/award-sheet: what spectators see (or would see) + publish state */
export const AdminAwardSheetSchema = PublicAwardSheetSchema.extend({ published: z.boolean() });
export type AdminAwardSheet = z.infer<typeof AdminAwardSheetSchema>;
